import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SmartAction } from '@connect-x-app/shared-domain-models';
import { createColumnHelper, getCoreRowModel, RowSelectionState, useReactTable } from '@tanstack/react-table';

import { TableCell, TableHeader } from '../../../../../shared';
import { useDeleteSmartAction } from '../queries';
import { SMART_ACTIONS_COLUMN_SIZE } from './smartActionsTable.constants';

type Props = {
  data: SmartAction[];
  refetchSmartActions: () => Promise<unknown>;
  renderCheckbox: boolean;
};

export const useSmartActionContentTable = ({ data, refetchSmartActions, renderCheckbox }: Props) => {
  const { t } = useTranslation();

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const { deleteSmartActions, isPending: isDeleteSmartActionLoading } = useDeleteSmartAction();

  const columnHelper = createColumnHelper<SmartAction>();

  const columns = [
    columnHelper.accessor('name', {
      cell: props => <TableCell {...props} withCheckbox={renderCheckbox} />,
      header: props => <TableHeader {...props} title={t('SMART_ACTIONS_COLUMN_NAME')} withCheckbox={renderCheckbox} />,
      id: 'Name',
      size: SMART_ACTIONS_COLUMN_SIZE.NAME,
    }),
    columnHelper.accessor('description', {
      cell: props => <TableCell {...props} />,
      header: props => <TableHeader {...props} title={t('SMART_ACTIONS_COLUMN_DESCRIPTION')} />,
      id: 'Description',
      size: SMART_ACTIONS_COLUMN_SIZE.DESCRIPTION,
    }),
  ];

  const table = useReactTable({
    columns,
    data,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.id,
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
    },
  });

  const itemsToDelete = Object.keys(rowSelection);

  return {
    deleteButtonDisabled: itemsToDelete.length === 0,
    deleteSmartActions: async () => {
      await deleteSmartActions(itemsToDelete);
      setRowSelection({});
      await refetchSmartActions();
    },
    isDeleteSmartActionLoading,
    table,
  };
};
